import React, { useMemo, useState } from 'react';
import { Card } from 'react-bootstrap';

import { usePokCore } from '../../../common/hooks/usePokCore';
import {
  CreateUpdateTooltipDto,
  GetTooltipDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import {
  convert,
  validate,
} from '../../../common/pokCore/contexts/TooltipContext';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { useTooltipDisplay } from '../../../common/pokCore/TooltipDisplayProvider';
import { TooltipSearch } from '../../components/Tooltips/TooltipSearch';
import { TooltipModalEditor } from '../../components/Tooltips/TooltipModalEditor';

export const TooltipsView: React.FC = () => {
  const pok = usePokCore();
  const { refreshTooltips } = useTooltipDisplay();
  const [tooltip, setTooltip] = useState<GetTooltipDto>();
  const [show, setShow] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const { t, tk } = useTranslation('administrationLng');

  const editTooltip = async (dto: CreateUpdateTooltipDto, id: string) => {
    const status = await validate(dto);
    if (status.valid) {
      try {
        await pok.tooltips.update(id, dto);
        setShow(false);
        setRefresh(!refresh);
        refreshTooltips();
        return { saved: true, errors: [] };
      } catch (response) {
        return {
          saved: false,
          errors: await responseErrors(response as ResponseError),
        };
      }
    } else {
      return { saved: false, errors: status.errors };
    }
  };

  const onRowClick = async (id: string) => {
    const item = await pok.tooltips.getById(id);
    setTooltip(item);
    setShow(true);
  };

  const handleClose = () => {
    setTooltip(undefined);
    setShow(false);
  };

  const getData = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.tooltips.getAllByPortion(
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
        )) as GridGetterFunction,
    [pok.tooltips],
  );

  const getCount = useMemo(
    () =>
      ((filterText, filterColumns) =>
        pok.tooltips.getAllCount(
          filterText,
          filterColumns,
        )) as GridCountFunction,
    [pok.tooltips],
  );

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">{t(tk.tooltips.tooltips)}</Card.Title>
        <TooltipSearch
          getData={getData}
          getCount={getCount}
          onRowClick={onRowClick}
          refresh={refresh}
        />
        {tooltip && (
          <TooltipModalEditor
            id={tooltip.id}
            tooltipKey={tooltip.key}
            description={tooltip.description}
            tooltip={convert(tooltip)}
            show={show}
            handleClose={handleClose}
            onCompleted={editTooltip}
          />
        )}
      </Card.Body>
    </Card>
  );
};
